/**
 * UltraAdminOrganizerProductsPage
 * ─────────────────────────────────
 * Ultra Admin, COP 31 organizatörünün stand paketlerinde kullanacağı ürünleri
 * (TV, halı, sandalye, PVC vb.) buradan yönetir.
 *
 * Features:
 *  • List + search + filters (active/passive, category)
 *  • Create / Edit modal with image upload (max 5 images)
 *  • Soft delete + restore
 *  • SKU uniqueness
 *  • Extra price (EUR/TRY/USD) — paket içinde ücretsiz, paket dışı ilavede gösterilir
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/apiClient';
import {
  Package, Plus, Search, Edit2, Trash2, X, Upload, Star, Eye, EyeOff,
  Image as ImageIcon, RotateCcw, Sparkles
} from 'lucide-react';
import AdminSidebar from '../components/AdminSidebar';
import UltraAdminTopbar from '../components/UltraAdminTopbar';
import BilingualField from '../components/BilingualField';
import ConfirmDialog from '../components/ConfirmDialog';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { compressImage } from '../utils/compressImage';

const getAuth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('cop31_ultra_token') || localStorage.getItem('cop31_token')}` }
});

const CURRENCIES = ['EUR', 'USD', 'TRY', 'GBP'];
const UNITS_TR = ['adet', 'metre', 'm²', 'metrekare', 'set', 'paket', 'koli', 'kg'];

export default function UltraAdminOrganizerProductsPage() {
  const { language } = useLanguage();
  const tr = language === 'tr';
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('all'); // all | active | passive
  const [filterCategory, setFilterCategory] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, product obj = edit
  const [categories, setCategories] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null); // product object to delete

  // ── Auth guard ──
  useEffect(() => {
    const token = localStorage.getItem('cop31_ultra_token') || localStorage.getItem('cop31_token');
    if (!token) navigate('/giris?tab=ultra');
  }, [navigate]);

  // ── Load products ──
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterActive === 'active') params.is_active = true;
      else if (filterActive === 'passive') params.is_active = false;
      if (filterCategory) params.category = filterCategory;
      if (search) params.search = search;
      const r = await apiClient.get(`/ultra-admin/organizer-products`, { params });
      setProducts(r.data || []);
    } catch (e) {
      console.error(e);
      toast.error(tr ? 'Ürünler yüklenemedi.' : 'Failed to load products.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const r = await apiClient.get(`/ultra-admin/organizer-products/categories`);
      setCategories(r.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchProducts(); /* eslint-disable-next-line */ }, [filterActive, filterCategory]);
  useEffect(() => { fetchCategories(); /* eslint-disable-next-line */ }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts();
  };

  const openCreate = () => { setEditing(null); setShowModal(true); };
  const openEdit = (p) => { setEditing(p); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditing(null); fetchProducts(); fetchCategories(); };

  const handleDelete = (p) => setConfirmDelete(p);

  const performDelete = async () => {
    if (!confirmDelete) return;
    try {
      await apiClient.delete(`/ultra-admin/organizer-products/${confirmDelete.id}`);
      toast.success(tr ? 'Ürün kalıcı olarak silindi.' : 'Product permanently deleted.');
      setConfirmDelete(null);
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const handleToggleActive = async (p) => {
    try {
      await apiClient.put(`/ultra-admin/organizer-products/${p.id}`, { is_active: !p.is_active });
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  // Self-heal: when a card image fails to load (S3 object missing or DB row
  // soft-deleted out of band), call the backend cleanup-orphans endpoint
  // and re-fetch the product list. We dedupe per product so a single failed
  // render does not fire the request twice.
  const cleanupAttempted = useRef(new Set());
  const handleBrokenImage = useCallback(async (productId) => {
    if (!productId || cleanupAttempted.current.has(productId)) return;
    cleanupAttempted.current.add(productId);
    try {
      const r = await apiClient.post(`/ultra-admin/organizer-products/${productId}/images/cleanup-orphans`);
      if (r?.data?.removed_count > 0) {
        // Silent re-fetch — no toast spam.
        fetchProducts();
      }
    } catch { /* swallow — best-effort cleanup */ }
  }, []);

  // Manual global cleanup: triggered by toolbar button. Scans every
  // organizer-product image and soft-deletes the ones whose S3 objects
  // are missing on both display & thumb keys.
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const handleCleanupAllOrphans = async () => {
    if (cleanupRunning) return;
    setCleanupRunning(true);
    try {
      const r = await apiClient.post(`/ultra-admin/organizer-products/cleanup-all-orphans`);
      const removed = r?.data?.removed_count ?? 0;
      const scanned = r?.data?.scanned_count ?? 0;
      if (removed > 0) {
        toast.success(tr
          ? `${removed} bozuk görsel temizlendi (${scanned} görsel tarandı).`
          : `${removed} broken image(s) cleaned (${scanned} scanned).`);
        cleanupAttempted.current = new Set();
        fetchProducts();
      } else {
        toast.success(tr
          ? `Bozuk görsel bulunamadı (${scanned} görsel tarandı).`
          : `No broken images found (${scanned} scanned).`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Temizlik başarısız.' : 'Cleanup failed.'));
    } finally {
      setCleanupRunning(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 overflow-auto">
        <UltraAdminTopbar
          title={tr ? 'Organizatör Ürünleri' : 'Organizer Products'}
          subtitle={tr ? 'Modüler stand paketlerinde kullanılacak organizatör ürünleri' : 'Organizer-owned items used inside modular stand packages'}
          icon={Package}
          onRefresh={fetchProducts}
          rightExtra={
            <div className="flex items-center gap-2">
              <button
                onClick={handleCleanupAllOrphans}
                disabled={cleanupRunning}
                data-testid="cleanup-orphan-images-btn"
                title={tr ? 'S3\'te eksik olan tüm yetim görselleri temizle' : 'Sweep all orphan images missing from S3'}
                className="flex items-center gap-2 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 font-semibold px-3 py-2 rounded-xl text-sm transition-colors disabled:opacity-60"
              >
                <Sparkles className={`h-4 w-4 ${cleanupRunning ? 'animate-pulse' : ''}`} />
                {cleanupRunning
                  ? (tr ? 'Taranıyor...' : 'Scanning...')
                  : (tr ? 'Bozuk Görselleri Temizle' : 'Clean Broken Images')}
              </button>
              <button
                onClick={openCreate}
                data-testid="create-organizer-product-btn"
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded-xl text-sm transition-colors shadow-sm"
              >
                <Plus className="h-4 w-4" /> {tr ? 'Yeni Ürün' : 'New Product'}
              </button>
            </div>
          }
        />

        <div className="px-6 pt-5">
          <PageIntro {...getPageIntro('organizer-products', language)} />
        </div>

        {/* Toolbar */}
        <div className="px-8 py-4 bg-white border-b border-gray-100">
          <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={tr ? 'Ad veya SKU ile ara...' : 'Search by name or SKU...'}
                data-testid="organizer-products-search"
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
              />
            </div>
            <select
              value={filterActive}
              onChange={e => setFilterActive(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 bg-white"
            >
              <option value="all">{tr ? 'Tümü' : 'All'}</option>
              <option value="active">{tr ? 'Aktif' : 'Active'}</option>
              <option value="passive">{tr ? 'Pasif' : 'Passive'}</option>
            </select>
            <select
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 bg-white"
            >
              <option value="">{tr ? 'Tüm kategoriler' : 'All categories'}</option>
              {categories.map(c => (
                <option key={c.category} value={c.category}>{c.category}</option>
              ))}
            </select>
            <button
              type="submit"
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium px-4 py-2 rounded-xl text-sm transition-colors"
            >
              {tr ? 'Filtrele' : 'Filter'}
            </button>
          </form>
        </div>

        {/* Content */}
        <div className="p-8">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin h-10 w-10 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-3" />
              <p className="text-gray-500 text-sm">{tr ? 'Yükleniyor...' : 'Loading...'}</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <Package className="h-14 w-14 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 mb-3">{tr ? 'Henüz ürün eklenmemiş.' : 'No products yet.'}</p>
              <button
                onClick={openCreate}
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded-xl text-sm"
              >
                <Plus className="h-4 w-4" /> {tr ? 'İlk Ürünü Ekle' : 'Add First Product'}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {products.map(p => (
                <div
                  key={p.id}
                  data-testid={`product-card-${p.id}`}
                  className={`bg-white rounded-2xl border overflow-hidden transition-all hover:shadow-lg ${p.is_active ? 'border-gray-200' : 'border-gray-200 opacity-70'}`}
                >
                  {/* Image */}
                  <div className="relative aspect-[4/3] bg-gray-100">
                    {(p.thumb_url || p.cover_url) ? (
                      <img
                        src={p.thumb_url || p.cover_url}
                        alt={p.name}
                        loading="lazy"
                        decoding="async"
                        onError={() => handleBrokenImage(p.id)}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="h-12 w-12 text-gray-300" />
                      </div>
                    )}
                    {!p.is_active && (
                      <span className="absolute top-2 right-2 bg-gray-900/80 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        {tr ? 'PASİF' : 'INACTIVE'}
                      </span>
                    )}
                  </div>
                  {/* Body */}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-bold text-gray-900 text-sm line-clamp-2 flex-1">
                        {(tr ? (p.name_tr || p.name) : (p.name_en || p.name)) || p.name}
                      </h3>
                    </div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">
                      {(tr ? (p.category_tr || p.category) : (p.category_en || p.category)) || '—'}
                      {(tr ? (p.sub_category_tr || p.sub_category) : (p.sub_category_en || p.sub_category))
                        ? ` · ${tr ? (p.sub_category_tr || p.sub_category) : (p.sub_category_en || p.sub_category)}`
                        : ''}
                    </p>
                    {p.sku && (
                      <p className="text-xs text-gray-500 mb-2 font-mono">SKU: {p.sku}</p>
                    )}
                    <div className="flex items-center justify-between bg-orange-50 border border-orange-100 rounded-xl px-3 py-2 mb-3">
                      <span className="text-[10px] font-bold text-orange-700 uppercase tracking-wider">
                        {tr ? 'Ekstra Fiyat' : 'Extra Price'}
                      </span>
                      <span className="font-bold text-orange-900 text-sm">
                        {Number(p.extra_price || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} {p.currency || 'EUR'} / {p.unit || 'adet'}
                      </span>
                    </div>
                    {p.track_stock && (
                      <div className={`flex items-center justify-between border rounded-xl px-3 py-1.5 mb-3 ${(p.stock_qty || 0) > 0 ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${(p.stock_qty || 0) > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {tr ? 'Stok' : 'Stock'}
                        </span>
                        <span className={`font-black text-xs ${(p.stock_qty || 0) > 0 ? 'text-emerald-900' : 'text-red-900'}`}>
                          {(p.stock_qty || 0) > 0
                            ? `${p.stock_qty} ${p.unit || 'adet'}`
                            : (tr ? 'Tükendi' : 'Out of stock')}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => openEdit(p)}
                        data-testid={`edit-product-${p.id}`}
                        className="flex-1 flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-xs py-2 rounded-lg transition-colors"
                      >
                        <Edit2 className="h-3.5 w-3.5" /> {tr ? 'Düzenle' : 'Edit'}
                      </button>
                      <button
                        onClick={() => handleToggleActive(p)}
                        className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                        title={p.is_active ? (tr ? 'Pasifleştir' : 'Deactivate') : (tr ? 'Aktifleştir' : 'Activate')}
                      >
                        {p.is_active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        onClick={() => handleDelete(p)}
                        className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
                        title={tr ? 'Sil' : 'Delete'}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <ProductFormModal
          tr={tr}
          product={editing}
          onClose={closeModal}
          existingCategories={categories}
        />
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!confirmDelete}
        title={tr ? 'Ürünü Kalıcı Olarak Sil' : 'Permanently Delete Product'}
        message={tr
          ? `"${(confirmDelete?.name_tr || confirmDelete?.name) || ''}" ürününü kalıcı olarak silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz. Ürün tüm modüler paketlerden de kaldırılacaktır.`
          : `Are you sure you want to permanently delete "${(confirmDelete?.name_en || confirmDelete?.name) || ''}"?\n\nThis action cannot be undone. The product will be removed from all modular packages.`
        }
        confirmLabel={tr ? 'Evet, Kalıcı Sil' : 'Yes, Delete Permanently'}
        cancelLabel={tr ? 'Vazgeç' : 'Cancel'}
        variant="danger"
        onConfirm={performDelete}
        onCancel={() => setConfirmDelete(null)}
        testId="confirm-delete-product"
      />
    </div>
  );
}

/* ─── Create/Edit Modal ─────────────────────────────────────────────────────── */
function ProductFormModal({ tr, product, onClose, existingCategories }) {
  const isEdit = !!product;
  const [form, setForm] = useState({
    name_tr:         product?.name_tr || product?.name || '',
    name_en:         product?.name_en || product?.name || '',
    description_tr:  product?.description_tr || product?.description || '',
    description_en:  product?.description_en || product?.description || '',
    category_tr:     product?.category_tr || product?.category || '',
    category_en:     product?.category_en || product?.category || '',
    sub_category_tr: product?.sub_category_tr || product?.sub_category || '',
    sub_category_en: product?.sub_category_en || product?.sub_category || '',
    category_id:     product?.category_id || '',
    sub_category_id: product?.sub_category_id || '',
    sku:             product?.sku || '',
    unit:            product?.unit || 'adet',
    extra_price:     product?.extra_price ?? 0,
    currency:        product?.currency || 'EUR',
    is_active:       product?.is_active ?? true,
    notes:           product?.notes || '',
    extra_min_qty:   product?.extra_min_qty ?? 1,
    extra_max_qty:   product?.extra_max_qty ?? '',
    track_stock:     product?.track_stock ?? false,
    stock_qty:       product?.stock_qty ?? 0,
    min_stock_threshold: product?.min_stock_threshold ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [images, setImages] = useState(product?.image_objects || []);
  // Yeni ürün için: Save'den ÖNCE lokalde tutulan dosyalar (File objeleri + previewURL)
  const [pendingImages, setPendingImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [catalogCategories, setCatalogCategories] = useState([]);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await apiClient.get(`/ultra-admin/organizer-categories`);
        setCatalogCategories(r.data || []);
      } catch { /* ignore */ }
    };
    load();
  }, []);

  // Seçili kategoriye ait alt kategoriler
  const selectedCategory = catalogCategories.find(c => c.id === form.category_id);
  const availableSubCategories = selectedCategory?.sub_categories || [];

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setErrors(e => { const n = { ...e }; delete n[k]; return n; });
  };

  const validate = () => {
    const e = {};
    const hasTr = !!form.name_tr.trim();
    const hasEn = !!form.name_en.trim();
    if (!hasTr && !hasEn) {
      e.name = tr ? 'Ürün adı hem TR hem EN olarak girilmelidir.' : 'Product name required in both TR and EN.';
    } else if (!hasTr) {
      e.name = tr ? 'TR (Türkçe) ürün adı zorunludur.' : 'TR (Turkish) product name is required.';
    } else if (!hasEn) {
      e.name = tr ? 'EN (İngilizce) ürün adı zorunludur.' : 'EN (English) product name is required.';
    }
    const ep = Number(form.extra_price);
    if (form.extra_price !== '' && (isNaN(ep) || ep < 0)) {
      e.extra_price = tr ? 'Geçersiz fiyat.' : 'Invalid price.';
    }
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        extra_price:   Number(form.extra_price || 0),
        extra_min_qty: Number(form.extra_min_qty || 1),
        extra_max_qty: form.extra_max_qty === '' ? null : Number(form.extra_max_qty),
        stock_qty:     form.track_stock ? Number(form.stock_qty || 0) : null,
        min_stock_threshold: form.track_stock ? Number(form.min_stock_threshold || 0) : null,
      };
      let productId = product?.id;

      if (isEdit) {
        await apiClient.put(`/ultra-admin/organizer-products/${productId}`, payload);
      } else {
        const res = await apiClient.post(`/ultra-admin/organizer-products`, payload);
        productId = res.data.id;
      }

      // Yeni ürün: bekleyen resimleri sırayla yükle
      if (!isEdit && pendingImages.length > 0) {
        setUploading(true);
        for (const p of pendingImages) {
          try {
            const fd = new FormData();
            const compressed = await compressImage(p.file);
            fd.append('image', compressed);
            await apiClient.post(`/ultra-admin/organizer-products/${productId}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          } catch (err) {
            toast.error(err.response?.data?.detail || (tr ? 'Görsel yüklenemedi.' : 'Upload failed.'));
          }
        }
        setUploading(false);
      }

      toast.success(tr ? 'Kaydedildi.' : 'Saved.');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  // Dosya seçimi: yeni üründe bekleyen listeye ekle, edit modunda direkt yükle
  const handleImagePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const totalExisting = isEdit ? images.length : (images.length + pendingImages.length);
    if (totalExisting + files.length > 5) {
      toast.error(tr ? 'En fazla 5 görsel yükleyebilirsiniz.' : 'Max 5 images allowed.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (!isEdit) {
      // Yeni ürün: sadece önizle, save'de gerçek upload yapılacak
      const newPending = files.map(file => ({
        file,
        previewUrl: URL.createObjectURL(file),
        _localId:   `${Date.now()}-${file.name}-${Math.random()}`,
      }));
      setPendingImages(prev => [...prev, ...newPending]);
    } else {
      // Edit: anlık upload
      setUploading(true);
      for (const file of files) {
        try {
          const fd = new FormData();
          const compressed = await compressImage(file);
          fd.append('image', compressed);
          await apiClient.post(`/ultra-admin/organizer-products/${product.id}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (err) {
          toast.error(err.response?.data?.detail || (tr ? 'Görsel yüklenemedi.' : 'Upload failed.'));
        }
      }
      try {
        const r = await apiClient.get(`/ultra-admin/organizer-products/${product.id}`);
        setImages(r.data.image_objects || []);
      } catch { /* ignore */ }
      setUploading(false);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePending = (localId) => {
    setPendingImages(prev => {
      const target = prev.find(p => p._localId === localId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter(p => p._localId !== localId);
    });
  };

  const handleImageDelete = async (imgId) => {
    if (!isEdit) return;
    try {
      await apiClient.delete(`/ultra-admin/organizer-products/${product.id}/images/${imgId}`);
      const r = await apiClient.get(`/ultra-admin/organizer-products/${product.id}`);
      setImages(r.data.image_objects || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleSetCover = async (imgId) => {
    if (!isEdit) return;
    try {
      await apiClient.post(`/ultra-admin/organizer-products/${product.id}/images/${imgId}/set-cover`, {});
      const r = await apiClient.get(`/ultra-admin/organizer-products/${product.id}`);
      setImages(r.data.image_objects || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="organizer-product-modal">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-amber-50">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-orange-500 flex items-center justify-center">
              <Package className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 text-lg">
                {isEdit ? (tr ? 'Ürünü Düzenle' : 'Edit Product') : (tr ? 'Yeni Ürün' : 'New Product')}
              </h2>
              <p className="text-xs text-gray-500">
                {tr ? 'Modüler stand paketlerinde kullanılır.' : 'Used inside modular stand packages.'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/50 text-gray-600" data-testid="close-modal-btn">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Name (TR/EN) */}
          <BilingualField
            label={tr ? 'Ürün Adı' : 'Product Name'}
            required
            error={errors.name}
            valueTr={form.name_tr}
            valueEn={form.name_en}
            onChangeTr={v => set('name_tr', v)}
            onChangeEn={v => set('name_en', v)}
            placeholderTr="Örn: 55&quot; LED Ekran"
            placeholderEn='E.g. 55" LED Screen'
            testIdBase="product-name"
            focusRingClass="focus:ring-orange-200"
          />

          {/* Description (TR/EN) */}
          <BilingualField
            label={tr ? 'Açıklama' : 'Description'}
            as="textarea"
            rows={3}
            valueTr={form.description_tr}
            valueEn={form.description_en}
            onChangeTr={v => set('description_tr', v)}
            onChangeEn={v => set('description_en', v)}
            placeholderTr="Kısa ürün açıklaması..."
            placeholderEn="Short product description..."
            testIdBase="product-description"
            focusRingClass="focus:ring-orange-200"
          />

          {/* Category & Sub-Category — from centrally managed list */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                {tr ? 'Kategori' : 'Category'}
              </label>
              <select
                value={form.category_id}
                onChange={e => {
                  const id = e.target.value;
                  const c  = catalogCategories.find(cc => cc.id === id);
                  setForm(f => ({
                    ...f,
                    category_id:     id,
                    category_tr:     c?.name_tr || '',
                    category_en:     c?.name_en || '',
                    sub_category_id: '',
                    sub_category_tr: '',
                    sub_category_en: '',
                  }));
                }}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 bg-white"
                data-testid="product-category-select"
              >
                <option value="">{tr ? '— Seçiniz —' : '— Select —'}</option>
                {catalogCategories.map(c => (
                  <option key={c.id} value={c.id}>
                    {tr ? (c.name_tr || c.name_en) : (c.name_en || c.name_tr)}
                  </option>
                ))}
              </select>
              {catalogCategories.length === 0 && (
                <p className="text-[11px] text-amber-700 mt-1">
                  {tr
                    ? 'Henüz kategori yok. "Ürün Kategorileri" sayfasından ekleyin.'
                    : 'No categories yet. Add some from "Product Categories" page.'}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                {tr ? 'Alt Kategori' : 'Sub-Category'}
              </label>
              <select
                value={form.sub_category_id}
                onChange={e => {
                  const id = e.target.value;
                  const s  = availableSubCategories.find(ss => ss.id === id);
                  setForm(f => ({
                    ...f,
                    sub_category_id: id,
                    sub_category_tr: s?.name_tr || '',
                    sub_category_en: s?.name_en || '',
                    // Auto-fill product name from sub-category (editable afterwards)
                    name_tr: s?.name_tr || f.name_tr || '',
                    name_en: s?.name_en || f.name_en || '',
                  }));
                  setErrors(e2 => { const n = { ...e2 }; delete n.name; return n; });
                }}
                disabled={!form.category_id || availableSubCategories.length === 0}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 bg-white disabled:bg-gray-50 disabled:text-gray-400"
                data-testid="product-sub-category-select"
              >
                <option value="">
                  {!form.category_id
                    ? (tr ? 'Önce kategori seçin' : 'Select a category first')
                    : availableSubCategories.length === 0
                      ? (tr ? '(Alt kategori yok)' : '(No sub-categories)')
                      : (tr ? '— Seçiniz —' : '— Select —')}
                </option>
                {availableSubCategories.map(s => (
                  <option key={s.id} value={s.id}>
                    {tr ? (s.name_tr || s.name_en) : (s.name_en || s.name_tr)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* SKU & Unit */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="SKU">
              <input
                type="text"
                value={form.sku}
                onChange={e => set('sku', e.target.value)}
                placeholder="TV-55-FHD"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 font-mono"
              />
            </Field>
            <Field label={tr ? 'Birim' : 'Unit'}>
              <select
                value={form.unit}
                onChange={e => set('unit', e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 bg-white"
              >
                {UNITS_TR.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </Field>
          </div>

          {/* Extra Price & Currency */}
          <div className="grid grid-cols-3 gap-3">
            <Field label={tr ? 'Ekstra Fiyat' : 'Extra Price'} error={errors.extra_price} className="col-span-2">
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.extra_price}
                onChange={e => set('extra_price', e.target.value)}
                placeholder="0.00"
                className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${errors.extra_price ? 'border-red-300 focus:ring-red-200' : 'border-gray-200 focus:ring-orange-200'}`}
                data-testid="product-extra-price-input"
              />
            </Field>
            <Field label={tr ? 'Para Birimi' : 'Currency'}>
              <select
                value={form.currency}
                onChange={e => set('currency', e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 bg-white"
              >
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <p className="text-xs text-gray-500 -mt-3 leading-relaxed">
            {tr
              ? 'Paket içinde kalan adetler için fiyat gösterilmez. Müşteri pakete ekstra eklerse bu fiyat üzerinden hesaplanır.'
              : 'Included package quantities are not priced to the customer. Extras added by the customer are charged at this unit price.'}
          </p>

          {/* Extras min/max limits */}
          <div className="border border-gray-100 rounded-2xl p-4 bg-gray-50/50">
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">
              {tr ? 'Ekstra Sipariş Limitleri' : 'Extra Order Limits'}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Field label={tr ? 'Min. Adet' : 'Min. Qty'}>
                <input
                  type="number" min={1} step={1}
                  value={form.extra_min_qty}
                  onChange={e => set('extra_min_qty', e.target.value)}
                  placeholder="1"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
                  data-testid="product-extra-min-qty"
                />
              </Field>
              <Field label={tr ? 'Max. Adet (boş = sınırsız)' : 'Max. Qty (empty = unlimited)'}>
                <input
                  type="number" min={0} step={1}
                  value={form.extra_max_qty}
                  onChange={e => set('extra_max_qty', e.target.value)}
                  placeholder={tr ? 'Sınırsız' : 'Unlimited'}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
                  data-testid="product-extra-max-qty"
                />
              </Field>
            </div>
            <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
              {tr
                ? 'Müşteri pakete ekstra eklerken bu limitler uygulanır.'
                : 'These limits are enforced when customers add extras on top of their package.'}
            </p>
          </div>

          {/* Stock tracking */}
          <div className="border border-gray-100 rounded-2xl p-4 bg-gray-50/50">
            <label className="flex items-center gap-2.5 cursor-pointer select-none mb-3">
              <input
                type="checkbox"
                checked={!!form.track_stock}
                onChange={e => set('track_stock', e.target.checked)}
                className="h-4 w-4 accent-orange-500"
                data-testid="product-track-stock-toggle"
              />
              <span className="text-sm font-semibold text-gray-800">
                {tr ? 'Stok Takibi Yapılsın Mı?' : 'Track Stock?'}
              </span>
            </label>
            {form.track_stock && (
              <div className="grid grid-cols-2 gap-3">
                <Field label={tr ? 'Stok Adedi' : 'Stock Qty'}>
                  <input
                    type="number" min={0} step={1}
                    value={form.stock_qty}
                    onChange={e => set('stock_qty', e.target.value)}
                    placeholder="0"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
                    data-testid="product-stock-qty"
                  />
                </Field>
                <Field label={tr ? 'Asgari Stok Uyarısı' : 'Min Stock Alert Level'}>
                  <input
                    type="number" min={0} step={1}
                    value={form.min_stock_threshold}
                    onChange={e => set('min_stock_threshold', e.target.value)}
                    placeholder="0"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
                    data-testid="product-min-stock-threshold"
                  />
                </Field>
              </div>
            )}
            <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
              {tr
                ? 'Ekstra talepler stok miktarını azaltır. Stok asgari seviyenin altına düştüğünde Ultra Admin\'e e-posta gönderilir. Sepete eklenen ekstralar 24 saat rezerve tutulur; 12. ve 23. saatte müşteriye hatırlatma e-postası gider; 24 saat sonra sepet boşalır ve stok geri eklenir.'
                : 'Extras deduct from stock. Ultra Admin is notified when stock falls below the alert level. Items added to cart are reserved for 24h; customer receives reminders at 12h and 23h; at 24h the cart clears and stock is restored.'}
            </p>
          </div>

          {/* Active toggle */}
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={e => set('is_active', e.target.checked)}
              className="h-4 w-4 accent-orange-500"
            />
            <span className="text-sm font-medium text-gray-700">
              {tr ? 'Aktif (paketlerde görünür)' : 'Active (visible in packages)'}
            </span>
          </label>

          {/* Notes */}
          <Field label={tr ? 'İç Notlar' : 'Internal Notes'}>
            <textarea
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={2}
              placeholder={tr ? 'Opsiyonel iç notlar...' : 'Optional internal notes...'}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 resize-none"
            />
          </Field>

          {/* Images section */}
          <div className="border-t border-gray-100 pt-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                {tr ? 'Görseller' : 'Images'}
                <span className="text-xs text-gray-400 font-normal">({images.length + pendingImages.length}/5)</span>
              </h3>
              {(images.length + pendingImages.length) < 5 && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handleImagePick}
                    className="hidden"
                    data-testid="product-image-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-1.5 bg-orange-100 hover:bg-orange-200 text-orange-700 font-semibold px-3 py-1.5 rounded-lg text-xs transition-colors disabled:opacity-50"
                  >
                    <Upload className="h-3.5 w-3.5" />
                    {uploading ? (tr ? 'Yükleniyor...' : 'Uploading...') : (tr ? 'Görsel Ekle' : 'Add Image')}
                  </button>
                </>
              )}
            </div>
            {!isEdit && (
              <p className="text-[11px] text-gray-400 mb-2">
                {tr
                  ? 'Seçtiğiniz görseller "Kaydet" butonuna bastığınızda ürünle birlikte yüklenecek.'
                  : 'Selected images will be uploaded together when you click "Save".'}
              </p>
            )}
            {(images.length + pendingImages.length) === 0 ? (
              <div className="bg-gray-50 rounded-xl p-6 text-center border border-dashed border-gray-200">
                <ImageIcon className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                <p className="text-xs text-gray-400">
                  {tr ? 'Görsel yüklenmedi. JPG/PNG/WebP — en fazla 10 MB.' : 'No images. JPG/PNG/WebP — max 10 MB.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-5 gap-2">
                {/* Already uploaded (edit mode) */}
                {images.map(img => (
                  <div key={img.id} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
                    <img src={img.thumb_url || img.url} alt="" className="w-full h-full object-cover" />
                    {img.is_cover && (
                      <span className="absolute top-1 left-1 bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                        {tr ? 'KAPAK' : 'COVER'}
                      </span>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end justify-center gap-1 p-1 opacity-0 group-hover:opacity-100">
                      {!img.is_cover && (
                        <button
                          type="button"
                          onClick={() => handleSetCover(img.id)}
                          className="bg-white/90 hover:bg-white text-gray-800 rounded-md p-1"
                          title={tr ? 'Kapak yap' : 'Set as cover'}
                        >
                          <Star className="h-3 w-3" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleImageDelete(img.id)}
                        className="bg-red-500 hover:bg-red-600 text-white rounded-md p-1"
                        title={tr ? 'Sil' : 'Delete'}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
                {/* Pending (not yet uploaded) */}
                {pendingImages.map(p => (
                  <div key={p._localId} className="relative group aspect-square rounded-lg overflow-hidden border-2 border-amber-300 border-dashed">
                    <img src={p.previewUrl} alt="" className="w-full h-full object-cover" />
                    <span className="absolute top-1 left-1 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                      {tr ? 'BEKLİYOR' : 'PENDING'}
                    </span>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={() => removePending(p._localId)}
                        className="bg-red-500 hover:bg-red-600 text-white rounded-md p-1.5"
                        title={tr ? 'Kaldır' : 'Remove'}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 rounded-xl transition-colors"
          >
            {tr ? 'Kapat' : 'Close'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || uploading}
            data-testid="save-product-btn"
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2 rounded-xl text-sm transition-colors disabled:opacity-60 flex items-center gap-2"
          >
            {(saving || uploading)
              ? <><RotateCcw className="h-4 w-4 animate-spin" /> {tr ? 'Kaydediliyor...' : 'Saving...'}</>
              : (isEdit ? (tr ? 'Güncelle' : 'Update') : (tr ? 'Kaydet' : 'Save'))
            }
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, error, children, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
